import type { Variants, Transition } from "framer-motion";

export const springConfig = {
  standard: {
    type: "spring" as const,
    stiffness: 300,
    damping: 30,
  },
  snappy: {
    type: "spring" as const,
    stiffness: 400,
    damping: 25,
  },
  gentle: {
    type: "spring" as const,
    stiffness: 200,
    damping: 20,
  },
} satisfies Record<string, Transition>;

export const durations = {
  instant: 0.1,
  fast: 0.15,
  normal: 0.2,
  slow: 0.3,
} as const;

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: durations.normal },
  },
};

export const fadeInScale: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: durations.normal },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: durations.fast },
  },
};

export const fadeInSlideUp: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig.snappy,
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: durations.fast },
  },
};

export const fadeInSlideDown: Variants = {
  hidden: {
    opacity: 0,
    y: -10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig.snappy,
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: durations.fast },
  },
};

export const slideInLeft: Variants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig.standard,
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: { duration: durations.fast },
  },
};

export const slideInRight: Variants = {
  hidden: {
    opacity: 0,
    x: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig.standard,
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: { duration: durations.fast },
  },
};

export const expandCollapse: Variants = {
  collapsed: {
    height: 0,
    opacity: 0,
    overflow: "hidden" as const,
    transition: {
      height: springConfig.standard,
      opacity: { duration: durations.normal, ease: "easeOut" },
    },
  },
  expanded: {
    height: "auto",
    opacity: 1,
    overflow: "visible" as const,
    transition: {
      height: springConfig.standard,
      opacity: { duration: durations.slow, ease: "easeIn" },
    },
  },
};

export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

export const staggerItem: Variants = {
  hidden: {
    opacity: 0,
    y: -10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig.snappy,
  },
};

export const listItem: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: durations.normal },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    x: -20,
    transition: { duration: durations.fast },
  },
};

export const cardHover = {
  rest: {
    scale: 1,
    boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
  },
  hover: {
    scale: 1.02,
    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
    transition: { duration: durations.fast },
  },
} satisfies Variants;

export const iconSwap: Variants = {
  initial: { scale: 0, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0, opacity: 0 },
};

export const buttonTap = {
  scale: 0.95,
  transition: { duration: durations.instant },
};

export const buttonHover = {
  scale: 1.05,
  transition: { duration: durations.instant },
};

export const modalBackdrop: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: durations.normal },
  },
  exit: {
    opacity: 0,
    transition: { duration: durations.fast },
  },
};

export const modalContent: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: springConfig.standard,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: durations.fast },
  },
};

export const sheetFromBottom: Variants = {
  hidden: {
    y: "100%",
    opacity: 0,
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: springConfig.standard,
  },
  exit: {
    y: "100%",
    opacity: 0,
    transition: { duration: durations.normal },
  },
};

export const sheetFromLeft: Variants = {
  hidden: {
    x: "-100%",
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: springConfig.standard,
  },
  exit: {
    x: "-100%",
    opacity: 0,
    transition: { duration: durations.normal },
  },
};

export const sheetFromRight: Variants = {
  hidden: {
    x: "100%",
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: springConfig.standard,
  },
  exit: {
    x: "100%",
    opacity: 0,
    transition: { duration: durations.normal },
  },
};

export const checkboxCheck: Variants = {
  unchecked: {
    pathLength: 0,
    opacity: 0,
  },
  checked: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: durations.normal, ease: "easeOut" },
      opacity: { duration: durations.instant },
    },
  },
};

export const pulseHighlight: Variants = {
  initial: {
    backgroundColor: "transparent",
  },
  highlight: {
    backgroundColor: ["transparent", "var(--accent)", "transparent"],
    transition: { duration: 1.5, times: [0, 0.2, 1] },
  },
};

export const shimmer: Variants = {
  initial: {
    backgroundPosition: "-200% 0",
  },
  animate: {
    backgroundPosition: "200% 0",
    transition: {
      repeat: Infinity,
      duration: 1.5,
      ease: "linear",
    },
  },
};

export const layoutTransition: Transition = {
  layout: springConfig.standard,
};

export const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0 } },
  exit: { opacity: 0, transition: { duration: 0 } },
} satisfies Variants;

export function getVariants(
  variants: Variants,
  shouldReduceMotion: boolean | null,
): Variants {
  if (shouldReduceMotion) {
    return reducedMotionVariants;
  }
  return variants;
}
